/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import {
  Policy,
  PolicyCategory,
  PolicySubcategory,
  PolicyStatus,
  PolicyPriority,
  PolicyCosts,
  PolicyBenefits,
  PolicyROI,
  PolicyRiskAssessment,
  PolicyMetrics,
  PolicyViolation,
  PolicyTemplate,
  PolicyAssessment,
  PolicyGameEffect,
  PolicyChallenge,
  PolicyMilestone,
  PolicyInsight,
  PolicyAnalytics,
  ResourceRequirement,
  SystemChange,
  TrainingRequirement,
  CommunicationPlan,
  ImplementationPhase,
  AssessedRisk,
  RiskMitigationStrategy
} from '../types/policy';

export class PolicyManager {
  private policies: Map<string, Policy> = new Map();
  private templates: Map<string, PolicyTemplate> = new Map();
  private violations: Map<string, PolicyViolation> = new Map();
  private assessments: Map<string, PolicyAssessment> = new Map();
  private challenges: Map<string, PolicyChallenge> = new Map();
  private milestones: Map<string, PolicyMilestone> = new Map();
  private metrics: Map<string, PolicyMetrics> = new Map();
  private analytics: Map<string, PolicyAnalytics> = new Map();

  constructor() {
    this.initializeTemplates();
    this.initializeChallenges();
  }

  // Policy Creation and Management
  public createPolicy(templateId: string, customization: Partial<Policy>): Policy {
    const template = this.templates.get(templateId);
    if (!template) {
      throw new Error(`Policy template ${templateId} not found`);
    }

    const policy: Policy = {
      id: this.generateId(),
      name: customization.name || template.name,
      description: customization.description || template.description,
      category: customization.category || template.category,
      subcategory: customization.subcategory || template.subcategory,
      status: PolicyStatus.Draft,
      priority: customization.priority || PolicyPriority.Medium,

      content: customization.content || template.template,
      requirements: customization.requirements || template.commonRequirements,
      controls: customization.controls || template.defaultControls,

      implementationPlan: this.generateImplementationPlan(template),
      timeline: this.generateTimeline(template),
      dependencies: {
        prerequisites: [],
        conflicts: [],
        synergies: [],
        superseded: [],
        frameworks: []
      },

      costs: customization.costs || template.estimatedCost,
      benefits: this.generateBenefits(template),
      roi: this.calculateROI(template.estimatedCost, this.generateBenefits(template)),

      riskAssessment: this.generateRiskAssessment(template),

      regulatoryFrameworks: [],
      complianceRequirements: [],

      version: '1.0.0',
      createdBy: 'Player',
      createdAt: Date.now(),
      lastUpdated: Date.now(),

      effectivenessScore: 0,
      complianceScore: 0,
      implementationProgress: 0
    };

    this.policies.set(policy.id, policy);
    return policy;
  }

  public updatePolicy(policyId: string, updates: Partial<Policy>): Policy {
    const policy = this.policies.get(policyId);
    if (!policy) {
      throw new Error(`Policy ${policyId} not found`);
    }

    const updatedPolicy = { ...policy, ...updates, lastUpdated: Date.now() };
    this.policies.set(policyId, updatedPolicy);
    return updatedPolicy;
  }

  public approvePolicy(policyId: string, approver: string): Policy {
    const policy = this.policies.get(policyId);
    if (!policy) {
      throw new Error(`Policy ${policyId} not found`);
    }

    if (policy.status !== PolicyStatus.UnderReview) {
      throw new Error(`Policy ${policyId} must be under review to be approved`);
    }

    policy.status = PolicyStatus.Approved;
    policy.approvedBy = approver;
    policy.approvedAt = Date.now();

    this.policies.set(policyId, policy);
    return policy;
  }

  public implementPolicy(policyId: string, implementer: string): Policy {
    const policy = this.policies.get(policyId);
    if (!policy) {
      throw new Error(`Policy ${policyId} not found`);
    }

    if (policy.status !== PolicyStatus.Approved) {
      throw new Error(`Policy ${policyId} must be approved to be implemented`);
    }

    policy.status = PolicyStatus.Implemented;
    policy.implementedBy = implementer;
    policy.implementedAt = Date.now();

    this.policies.set(policyId, policy);
    return policy;
  }

  public enforcePolicy(policyId: string): Policy {
    const policy = this.policies.get(policyId);
    if (!policy) {
      throw new Error(`Policy ${policyId} not found`);
    }

    if (policy.status !== PolicyStatus.Implemented) {
      throw new Error(`Policy ${policyId} must be implemented to be enforced`);
    }

    policy.status = PolicyStatus.Enforced;
    this.policies.set(policyId, policy);
    return policy;
  }

  // Policy Analytics and Metrics
  public calculatePolicyMetrics(policyId: string): PolicyMetrics {
    const policy = this.policies.get(policyId);
    if (!policy) {
      throw new Error(`Policy ${policyId} not found`);
    }

    const violations = Array.from(this.violations.values()).filter(v => v.policyId === policyId);
    const assessments = Array.from(this.assessments.values()).filter(a => a.policyId === policyId);

    const complianceRate = this.calculateComplianceRate(policy);
    const effectivenessScore = this.calculateEffectivenessScore(policy);
    const implementationProgress = this.calculateImplementationProgress(policy);

    const metrics: PolicyMetrics = {
      policyId,
      complianceRate,
      effectivenessScore,
      implementationProgress,
      userSatisfaction: this.calculateUserSatisfaction(policy),
      auditFindings: this.getAuditFindingsCount(policy),
      violationsCount: violations.length,
      costEffectiveness: this.calculateCostEffectiveness(policy),
      lastUpdated: Date.now(),
      trends: {
        complianceTrend: this.getComplianceTrend(policy),
        effectivenessTrend: this.getEffectivenessTrend(policy),
        violationTrend: this.getViolationTrend(policy)
      }
    };

    this.metrics.set(policyId, metrics);
    return metrics;
  }

  public generatePolicyInsights(policyId: string): PolicyInsight[] {
    const policy = this.policies.get(policyId);
    const metrics = this.metrics.get(policyId);

    if (!policy || !metrics) {
      return [];
    }

    const insights: PolicyInsight[] = [];

    // Compliance insights
    if (metrics.complianceRate < 80) {
      insights.push({
        id: this.generateId(),
        type: 'Risk',
        title: 'Low Compliance Rate Detected',
        description: `Policy ${policy.name} has a compliance rate of ${metrics.complianceRate.toFixed(1)}%`,
        confidence: 0.85,
        impact: 'High',
        effort: 'Medium',
        actions: [
          'Review policy communication strategy',
          'Increase training frequency',
          'Strengthen enforcement mechanisms'
        ],
        data: { complianceRate: metrics.complianceRate },
        timestamp: Date.now()
      });
    }

    // Cost-effectiveness insights
    if (metrics.costEffectiveness < 0.5) {
      insights.push({
        id: this.generateId(),
        type: 'Opportunity',
        title: 'Cost Optimization Opportunity',
        description: `Policy ${policy.name} shows potential for cost reduction`,
        confidence: 0.75,
        impact: 'Medium',
        effort: 'High',
        actions: [
          'Review automation opportunities',
          'Optimize resource allocation',
          'Consider process simplification'
        ],
        data: { costEffectiveness: metrics.costEffectiveness },
        timestamp: Date.now()
      });
    }

    // Effectiveness insights
    if (metrics.effectivenessScore > 90) {
      insights.push({
        id: this.generateId(),
        type: 'Recommendation',
        title: 'High-Performing Policy Identified',
        description: `Policy ${policy.name} shows excellent effectiveness and could be expanded`,
        confidence: 0.90,
        impact: 'High',
        effort: 'Low',
        actions: [
          'Consider extending policy scope',
          'Use as best practice template',
          'Document success factors'
        ],
        data: { effectivenessScore: metrics.effectivenessScore },
        timestamp: Date.now()
      });
    }

    return insights;
  }

  // Policy Violation Management
  public reportViolation(violation: Omit<PolicyViolation, 'id' | 'detectionDate'>): PolicyViolation {
    const newViolation: PolicyViolation = {
      ...violation,
      id: this.generateId(),
      detectionDate: Date.now()
    };

    this.violations.set(newViolation.id, newViolation);

    // Update policy metrics
    this.calculatePolicyMetrics(violation.policyId);

    // Trigger enforcement actions if needed
    this.evaluateEnforcementActions(newViolation);

    return newViolation;
  }

  public resolveViolation(violationId: string, resolution: string): PolicyViolation {
    const violation = this.violations.get(violationId);
    if (!violation) {
      throw new Error(`Violation ${violationId} not found`);
    }

    violation.status = 'Resolved';
    violation.resolutionDate = Date.now();
    violation.longTermRemediation = resolution;

    this.violations.set(violationId, violation);
    return violation;
  }

  // Policy Assessment
  public conductAssessment(policyId: string, assessmentType: string): PolicyAssessment {
    const policy = this.policies.get(policyId);
    if (!policy) {
      throw new Error(`Policy ${policyId} not found`);
    }

    const assessment: PolicyAssessment = {
      id: this.generateId(),
      policyId,
      assessmentType: assessmentType as any,
      methodology: 'Automated Assessment',
      scope: 'Full Policy Review',
      criteria: this.generateAssessmentCriteria(policy),
      findings: this.generateAssessmentFindings(policy),
      recommendations: this.generateRecommendations(policy),
      overallScore: Math.random() * 100, // Simplified for now
      assessor: 'System',
      assessmentDate: Date.now(),
      nextAssessmentDate: Date.now() + (90 * 24 * 60 * 60 * 1000) // 90 days
    };

    this.assessments.set(assessment.id, assessment);
    return assessment;
  }

  // Game Integration
  public calculateGameEffects(policyId: string): PolicyGameEffect {
    const policy = this.policies.get(policyId);
    if (!policy) {
      throw new Error(`Policy ${policyId} not found`);
    }

    const baseEffects = this.getCategoryBaseEffects(policy.category);
    const effectivenessMultiplier = policy.effectivenessScore / 100;
    const implementationMultiplier = policy.implementationProgress / 100;

    const effects: PolicyGameEffect = {
      policyId,
      effects: {
        compliance: baseEffects.compliance * effectivenessMultiplier * implementationMultiplier,
        threat: baseEffects.threat * effectivenessMultiplier * implementationMultiplier,
        budget: -policy.costs.totalAnnualCost / 12, // Monthly cost
        efficiency: baseEffects.efficiency * effectivenessMultiplier,
        productivity: baseEffects.productivity * effectivenessMultiplier,
        customerSatisfaction: baseEffects.customerSatisfaction * effectivenessMultiplier,
        employeeMorale: baseEffects.employeeMorale * effectivenessMultiplier
      },
      duration: 30, // 30 days
      conditions: [
        {
          type: 'Metric',
          parameter: 'policyStatus',
          operator: 'equals',
          value: PolicyStatus.Enforced
        }
      ]
    };

    return effects;
  }

  public checkMilestones(): PolicyMilestone[] {
    const completedMilestones: PolicyMilestone[] = [];

    for (const [id, milestone] of this.milestones) {
      if (milestone.unlocked) continue;

      const policy = this.policies.get(milestone.policyId);
      if (!policy) continue;

      if (this.evaluateMilestoneCondition(milestone, policy)) {
        milestone.unlocked = true;
        milestone.completedDate = Date.now();
        completedMilestones.push(milestone);
      }
    }

    return completedMilestones;
  }

  public updateChallenges(): void {
    for (const [id, challenge] of this.challenges) {
      if (!challenge.isActive) continue;

      // Update challenge objectives
      for (const objective of challenge.objectives) {
        if (objective.achieved) continue;

        const currentValue = this.getObjectiveCurrentValue(objective);
        objective.achieved = this.evaluateObjective(objective, currentValue);
      }
    }
  }

  // Private helper methods
  private generateId(): string {
    return `policy_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private generateImplementationPlan(template: PolicyTemplate) {
    const phases: ImplementationPhase[] = [
      {
        id: this.generateId(),
        name: 'Planning and Design',
        description: 'Define detailed implementation approach',
        duration: 7,
        dependencies: [],
        deliverables: ['Implementation plan', 'Resource allocation'],
        resources: ['Policy team', 'Stakeholders'],
        risks: ['Resource constraints', 'Scope creep']
      },
      {
        id: this.generateId(),
        name: 'Development and Configuration',
        description: 'Implement system changes and configure controls',
        duration: 14,
        dependencies: ['Planning and Design'],
        deliverables: ['System changes', 'Control implementations'],
        resources: ['Development team', 'System administrators'],
        risks: ['Technical challenges', 'Integration issues']
      },
      {
        id: this.generateId(),
        name: 'Training and Communication',
        description: 'Train staff and communicate new requirements',
        duration: 7,
        dependencies: ['Development and Configuration'],
        deliverables: ['Training materials', 'Communication plan'],
        resources: ['Training team', 'Communications'],
        risks: ['Low adoption', 'Communication gaps']
      },
      {
        id: this.generateId(),
        name: 'Go-Live and Monitoring',
        description: 'Deploy policy and monitor effectiveness',
        duration: 14,
        dependencies: ['Training and Communication'],
        deliverables: ['Deployed policy', 'Monitoring reports'],
        resources: ['Operations team', 'Compliance team'],
        risks: ['Implementation issues', 'Compliance gaps']
      }
    ];

    return {
      phases,
      resourcesRequired: [],
      systemChanges: [],
      trainingRequirements: [],
      communicationPlan: []
    };
  }

  private generateTimeline(template: PolicyTemplate) {
    const now = Date.now();
    return {
      draftDate: now,
      reviewDate: now + (7 * 24 * 60 * 60 * 1000), // 1 week
      implementationStartDate: now + (21 * 24 * 60 * 60 * 1000), // 3 weeks
      implementationEndDate: now + (42 * 24 * 60 * 60 * 1000), // 6 weeks
      enforcementDate: now + (49 * 24 * 60 * 60 * 1000), // 7 weeks
      annualReviewDate: now + (180 * 24 * 60 * 60 * 1000) // 6 months
    };
  }

  private generateBenefits(template: PolicyTemplate): PolicyBenefits {
    return {
      riskReduction: {
        complianceRiskReduction: 25 + Math.random() * 25,
        operationalRiskReduction: 15 + Math.random() * 20,
        financialRiskReduction: 20 + Math.random() * 30,
        reputationalRiskReduction: 10 + Math.random() * 20
      },
      efficiencyGains: {
        processEfficiency: 10 + Math.random() * 15,
        resourceEfficiency: 5 + Math.random() * 10,
        costSavings: template.estimatedCost.totalAnnualCost * (0.1 + Math.random() * 0.2),
        productivityIncrease: 5 + Math.random() * 10
      },
      revenueOpportunities: {
        newMarkets: 0,
        competitiveAdvantage: 10000 + Math.random() * 50000,
        customerTrust: 5000 + Math.random() * 15000,
        brandValue: 2500 + Math.random() * 10000
      },
      totalQuantifiedBenefit: 20000 + Math.random() * 80000
    };
  }

  private calculateROI(costs: PolicyCosts, benefits: PolicyBenefits): PolicyROI {
    const totalBenefit = benefits.totalQuantifiedBenefit;
    const totalCost = costs.totalImplementationCost + (costs.totalAnnualCost * 3); // 3 year analysis

    return {
      paybackPeriod: Math.ceil(totalCost / (totalBenefit / 12)), // months
      netPresentValue: totalBenefit - totalCost,
      internalRateOfReturn: ((totalBenefit - totalCost) / totalCost) * 100,
      benefitCostRatio: totalBenefit / totalCost,
      riskAdjustedReturn: ((totalBenefit * 0.8) - (totalCost * 1.1)) / totalCost * 100, // Risk-adjusted
      confidenceLevel: 0.75
    };
  }

  private generateRiskAssessment(template: PolicyTemplate): PolicyRiskAssessment {
    const implementationRisks: AssessedRisk[] = [
      {
        id: this.generateId(),
        description: 'Resource constraints during implementation',
        category: 'Implementation',
        probability: 30 + Math.random() * 40,
        impact: 40 + Math.random() * 30,
        riskScore: 0,
        mitigationStrategy: 'Proper resource planning and allocation',
        residualRisk: 15
      },
      {
        id: this.generateId(),
        description: 'Resistance to change from staff',
        category: 'Implementation',
        probability: 50 + Math.random() * 30,
        impact: 30 + Math.random() * 20,
        riskScore: 0,
        mitigationStrategy: 'Change management and communication strategy',
        residualRisk: 20
      }
    ];

    // Calculate risk scores
    implementationRisks.forEach(risk => {
      risk.riskScore = (risk.probability * risk.impact) / 100;
    });

    return {
      implementationRisks,
      operationalRisks: [],
      complianceRisks: [],
      financialRisks: [],
      overallRiskScore: implementationRisks.reduce((sum, risk) => sum + risk.riskScore, 0) / implementationRisks.length,
      riskMitigationStrategies: []
    };
  }

  private getCategoryBaseEffects(category: PolicyCategory) {
    const effects = {
      [PolicyCategory.DataGovernance]: {
        compliance: 5, threat: -3, efficiency: 2, productivity: 1,
        customerSatisfaction: 3, employeeMorale: 1
      },
      [PolicyCategory.Security]: {
        compliance: 8, threat: -10, efficiency: -1, productivity: -2,
        customerSatisfaction: 2, employeeMorale: 0
      },
      [PolicyCategory.FinancialCompliance]: {
        compliance: 10, threat: -5, efficiency: -3, productivity: -3,
        customerSatisfaction: 1, employeeMorale: -1
      },
      [PolicyCategory.Operational]: {
        compliance: 3, threat: -2, efficiency: 8, productivity: 6,
        customerSatisfaction: 4, employeeMorale: 3
      },
      [PolicyCategory.HumanResources]: {
        compliance: 4, threat: -1, efficiency: 5, productivity: 7,
        customerSatisfaction: 2, employeeMorale: 8
      }
    };

    return effects[category] || {
      compliance: 2, threat: -1, efficiency: 2, productivity: 2,
      customerSatisfaction: 1, employeeMorale: 1
    };
  }

  private calculateComplianceRate(policy: Policy): number {
    // Simplified calculation based on implementation progress and violations
    const baseCompliance = policy.implementationProgress;
    const violationPenalty = Array.from(this.violations.values())
      .filter(v => v.policyId === policy.id).length * 5;

    return Math.max(0, Math.min(100, baseCompliance - violationPenalty));
  }

  private calculateEffectivenessScore(policy: Policy): number {
    // Simplified calculation based on ROI and benefits realization
    const roiScore = Math.min(100, policy.roi.riskAdjustedReturn / 2);
    const implementationScore = policy.implementationProgress;

    return (roiScore + implementationScore) / 2;
  }

  private calculateImplementationProgress(policy: Policy): number {
    if (!policy.implementationPlan) return 0;

    // Simplified calculation based on timeline
    if (policy.status === PolicyStatus.Enforced) return 100;
    if (policy.status === PolicyStatus.Implemented) return 90;
    if (policy.status === PolicyStatus.Approved) return 50;
    if (policy.status === PolicyStatus.UnderReview) return 25;

    return 0;
  }

  private calculateUserSatisfaction(policy: Policy): number {
    // Simplified calculation
    return 60 + Math.random() * 30;
  }

  private getAuditFindingsCount(policy: Policy): number {
    return Array.from(this.assessments.values())
      .filter(a => a.policyId === policy.id)
      .reduce((sum, assessment) => sum + assessment.findings.filter(f => f.category === 'Weakness').length, 0);
  }

  private calculateCostEffectiveness(policy: Policy): number {
    if (policy.costs.totalAnnualCost === 0) return 1;
    return policy.benefits.totalQuantifiedBenefit / (policy.costs.totalAnnualCost * 2);
  }

  private getComplianceTrend(policy: Policy): number[] {
    // Simplified trend data
    return Array.from({ length: 12 }, () => 75 + Math.random() * 20);
  }

  private getEffectivenessTrend(policy: Policy): number[] {
    return Array.from({ length: 12 }, () => 70 + Math.random() * 25);
  }

  private getViolationTrend(policy: Policy): number[] {
    return Array.from({ length: 12 }, () => Math.random() * 5);
  }

  private evaluateEnforcementActions(violation: PolicyViolation): void {
    // Logic to trigger automatic enforcement actions based on violation severity
    if (violation.severity === 'Critical') {
      // Immediate action required
      console.log(`Critical violation detected for policy ${violation.policyId}`);
    }
  }

  private generateAssessmentCriteria(policy: Policy) {
    return [
      {
        id: this.generateId(),
        description: 'Policy clarity and completeness',
        weight: 0.2,
        score: 80 + Math.random() * 20,
        maxScore: 100,
        evidence: ['Policy document', 'Stakeholder feedback'],
        comments: 'Policy is well-defined'
      },
      {
        id: this.generateId(),
        description: 'Implementation effectiveness',
        weight: 0.3,
        score: 75 + Math.random() * 25,
        maxScore: 100,
        evidence: ['Implementation reports', 'Metrics data'],
        comments: 'Good progress on implementation'
      }
    ];
  }

  private generateAssessmentFindings(policy: Policy) {
    return [
      {
        id: this.generateId(),
        category: 'Strength' as any,
        severity: 'Medium' as any,
        description: 'Strong stakeholder engagement',
        impact: 'Positive impact on policy adoption',
        evidence: ['Meeting records', 'Feedback surveys'],
        recommendationId: undefined
      }
    ];
  }

  private generateRecommendations(policy: Policy) {
    return [
      {
        id: this.generateId(),
        description: 'Increase monitoring frequency',
        priority: 'Medium' as any,
        effort: 'Low' as any,
        impact: 'Medium' as any,
        timeline: 30,
        resources: ['Compliance team'],
        responsibleParty: 'Compliance Manager',
        status: 'Open' as any
      }
    ];
  }

  private evaluateMilestoneCondition(milestone: PolicyMilestone, policy: Policy): boolean {
    // Simplified milestone evaluation
    switch (milestone.trigger) {
      case 'policy_implemented':
        return policy.status === PolicyStatus.Implemented || policy.status === PolicyStatus.Enforced;
      case 'high_compliance':
        return this.calculateComplianceRate(policy) >= 90;
      case 'effective_implementation':
        return policy.effectivenessScore >= 80;
      default:
        return false;
    }
  }

  private getObjectiveCurrentValue(objective: ChallengeObjective): number {
    // Simplified - would need game state integration
    return Math.random() * 100;
  }

  private evaluateObjective(objective: ChallengeObjective, currentValue: number): boolean {
    switch (objective.operator) {
      case 'greaterThan':
        return currentValue > objective.target;
      case 'lessThan':
        return currentValue < objective.target;
      case 'equals':
        return Math.abs(currentValue - objective.target) < 1;
      default:
        return false;
    }
  }

  private initializeTemplates(): void {
    // Initialize common policy templates
    const dataClassificationTemplate: PolicyTemplate = {
      id: 'data_classification_template',
      name: 'Data Classification Policy',
      description: 'Template for data classification and handling policies',
      category: PolicyCategory.DataGovernance,
      subcategory: PolicySubcategory.DataClassification,
      template: {
        purpose: 'To establish a framework for classifying data based on sensitivity',
        scope: 'All organizational data and information assets',
        applicability: ['All employees', 'Contractors', 'Partners'],
        policyStatement: 'All data must be classified according to its sensitivity level',
        definitions: {
          'Public': 'Data that can be freely shared',
          'Internal': 'Data for internal use only',
          'Confidential': 'Sensitive data requiring protection',
          'Restricted': 'Highly sensitive data with strict access controls'
        },
        responsibilities: [
          {
            role: 'Data Owner',
            department: 'Business Unit',
            responsibilities: ['Classify data', 'Define access requirements'],
            authorities: ['Approve access requests', 'Update classifications'],
            accountabilities: ['Data protection', 'Compliance with policy']
          }
        ],
        procedures: [],
        exceptions: [],
        references: ['ISO 27001', 'NIST SP 800-53']
      },
      defaultControls: [],
      commonRequirements: [],
      estimatedImplementationTime: 90,
      estimatedCost: {
        oneTimeCosts: {
          developmentCosts: 5000,
          implementationCosts: 3000,
          trainingCosts: 2000,
          technologyCosts: 4000,
          consultingCosts: 1500
        },
        recurringCosts: {
          maintenanceCosts: 1000,
          monitoringCosts: 500,
          trainingCosts: 300,
          licensingCosts: 200,
          personnelCosts: 2000
        },
        opportunityCosts: 1000,
        totalImplementationCost: 15500,
        totalAnnualCost: 4000
      },
      适用性: ['All organizations handling sensitive data']
    };

    this.templates.set(dataClassificationTemplate.id, dataClassificationTemplate);
  }

  private initializeChallenges(): void {
    // Initialize policy challenges
    const firstPolicyChallenge: PolicyChallenge = {
      id: 'first_policy_challenge',
      name: 'Policy Pioneer',
      description: 'Create and implement your first organizational policy',
      difficulty: 'Easy',
      objectives: [
        {
          id: 'create_policy',
          description: 'Create a new policy from template',
          metric: 'policies_created',
          target: 1,
          operator: 'greaterThan',
          weight: 50,
          achieved: false
        },
        {
          id: 'implement_policy',
          description: 'Successfully implement the policy',
          metric: 'policies_implemented',
          target: 1,
          operator: 'greaterThan',
          weight: 50,
          achieved: false
        }
      ],
      timeLimit: 30,
      rewards: [
        {
          type: 'Budget',
          value: 5000,
          description: 'Policy implementation bonus'
        },
        {
          type: 'Compliance',
          value: 10,
          description: 'Compliance score boost'
        }
      ],
      requirements: [
        {
          type: 'Building',
          value: 'Policy HQ',
          description: 'Requires Policy HQ building'
        }
      ],
      isActive: false
    };

    this.challenges.set(firstPolicyChallenge.id, firstPolicyChallenge);
  }

  // Public getters for game integration
  public getPolicies(): Policy[] {
    return Array.from(this.policies.values());
  }

  public getPolicy(id: string): Policy | undefined {
    return this.policies.get(id);
  }

  public getViolations(): PolicyViolation[] {
    return Array.from(this.violations.values());
  }

  public getChallenges(): PolicyChallenge[] {
    return Array.from(this.challenges.values());
  }

  public getMetrics(): PolicyMetrics[] {
    return Array.from(this.metrics.values());
  }

  public getTemplates(): PolicyTemplate[] {
    return Array.from(this.templates.values());
  }

  public getInsights(policyId: string): PolicyInsight[] {
    return this.generatePolicyInsights(policyId);
  }

  public activateChallenge(challengeId: string): void {
    const challenge = this.challenges.get(challengeId);
    if (challenge) {
      challenge.isActive = true;
    }
  }
}

// Export singleton instance
export const policyManager = new PolicyManager();